import React, { useCallback, useEffect, useState } from "react";
import {
  fetchStagingStatus,
  unlockStaging,
  STAGING_LOCKED_EVENT,
} from "../lib/stagingGate";
import { useTranslation } from "react-i18next";

// Outer staging-access wrapper. When the backend reports the gate is active
// and the current session is not unlocked, it renders a simple shared-password
// prompt (separate from the app's real /login). Otherwise it renders the app
// untouched. In production (STAGING_MODE off) status.gate_active is false, so
// this is a transparent pass-through.
export const StagingGate = ({ children }) => {
  const { t } = useTranslation();
  // "checking" | "open" | "locked"
  const [state, setState] = useState("checking");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const check = useCallback(async () => {
    try {
      const s = await fetchStagingStatus();
      if (!s.gate_active || s.unlocked) setState("open");
      else setState("locked");
    } catch {
      // If the status probe itself is gated or fails, err on the side of
      // showing the app's normal flow rather than hard-locking.
      setState("open");
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  useEffect(() => {
    const onLocked = () => setState("locked");
    window.addEventListener(STAGING_LOCKED_EVENT, onLocked);
    return () => window.removeEventListener(STAGING_LOCKED_EVENT, onLocked);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await unlockStaging(password);
      setPassword("");
      setState("open");
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Incorrect password. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (state === "checking") {
    return (
      <div
        data-testid="staging-gate-loading"
        style={{
          minHeight: "100vh", display: "flex", alignItems: "center",
          justifyContent: "center", background: "#0b0f14", color: "#7c8794",
          fontFamily: "ui-sans-serif, system-ui, sans-serif", fontSize: 13,
        }}
      >
        …
      </div>
    );
  }

  if (state === "locked") {
    return (
      <div
        data-testid="staging-gate"
        style={{
          minHeight: "100vh", display: "flex", alignItems: "center",
          justifyContent: "center", background: "#0b0f14",
          fontFamily: "ui-sans-serif, system-ui, sans-serif", padding: 24,
        }}
      >
        <form
          onSubmit={submit}
          data-testid="staging-gate-form"
          style={{
            width: "100%", maxWidth: 360, background: "#121821",
            border: "1px solid #1f2a37", borderRadius: 14, padding: 28,
            boxShadow: "0 20px 60px rgba(0,0,0,.45)",
          }}
        >
          <div style={{ color: "#e6edf3", fontSize: 16, fontWeight: 700,
            letterSpacing: "-0.01em" }}>
            {t("ui.staging_gate.title", { defaultValue: "Staging environment" })}
          </div>
          <div style={{ color: "#8b97a5", fontSize: 12.5, marginTop: 6,
            lineHeight: 1.5 }}>
            {t("ui.staging_gate.body", { defaultValue: "This is a private staging build. Enter the shared access password to continue. (This is not your account login.)" })}
          </div>
          <input
            type="password"
            data-testid="staging-gate-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("ui.staging_gate.placeholder", { defaultValue: "Access password" })}
            autoFocus
            style={{
              width: "100%", marginTop: 18, padding: "11px 12px",
              background: "#0b0f14", border: "1px solid #26323f",
              borderRadius: 9, color: "#e6edf3", fontSize: 14, outline: "none",
              boxSizing: "border-box",
            }}
          />
          {error && (
            <div data-testid="staging-gate-error"
              style={{ color: "#f87171", fontSize: 12.5, marginTop: 10 }}>
              {error}
            </div>
          )}
          <button
            type="submit"
            data-testid="staging-gate-submit"
            disabled={submitting || !password}
            style={{
              width: "100%", marginTop: 16, padding: "11px 12px",
              background: submitting || !password ? "#1b2733" : "#2563eb",
              color: "#fff", border: "none", borderRadius: 9, fontSize: 14,
              fontWeight: 600, cursor: submitting || !password ? "default" : "pointer",
            }}
          >
            {submitting ? t("ui.staging_gate.checking", { defaultValue: "Checking…" }) : t("ui.staging_gate.enter", { defaultValue: "Enter" })}
          </button>
        </form>
      </div>
    );
  }

  return children;
};
